/**
 * optimizationUtils.ts
 * Utilitaires partagés pour le calcul des métriques d'optimisation.
 */
import { OptimizationRun } from '../types/optimization'

/**
 * Calcule le taux de remplissage en poids (fraction 0..N).
 *
 * Convention stricte : retourne TOUJOURS une fraction (ex. 0.18, 1.20 pour une
 * surcharge de 120 %). NE clampons PAS à 1 — les dépassements doivent rester
 * visibles. Seule la longueur de la barre graphique peut être clampée au rendu.
 *
 * Sources (par priorité) :
 *  1. `result.fill_rate_weight` si présent et fini.
 *     - OptimizationExecutionService produit loadedWeight / maxWeight.
 *     - On accepte cette valeur TELLE QUELLE, quelle que soit sa magnitude.
 *     - NB : un résultat > 1 signifie surcharge ; ne jamais diviser par 100.
 *  2. Calcul depuis les placements si la valeur backend est absente.
 *
 * Retourne null si le poids max du conteneur n'est pas défini.
 */
export function computeFillRateWeight(run: OptimizationRun): number | null {
  const result = run.result
  if (!result) return null

  // 1. Utiliser la valeur backend telle quelle (fraction brute)
  const backendRate = result.fill_rate_weight ?? null
  if (backendRate !== null && Number.isFinite(backendRate) && backendRate >= 0) {
    return backendRate
  }

  // 2. Calculer depuis les placements
  const placements = result.placements ?? []
  const loadSpace = run.input?.loadSpace
  const inputConstraints = (run.input as any)?.constraints as any
  const maxWeight =
    (loadSpace as any)?.max_weight ??
    (loadSpace as any)?.maxWeight ??
    (inputConstraints as any)?.max_weight ??
    inputConstraints?.maxWeight ??
    null

  if (!maxWeight || maxWeight <= 0) return null

  const loadedWeight = placements.reduce((sum, p) => {
    const w = (p as any).weight ?? 0
    return sum + w
  }, 0)

  if (loadedWeight <= 0) {
    const inputItemIds = new Set(run.input.items.map((i) => i.id))
    const itemWeightMap = new Map(run.input.items.map((i) => [i.id, i.weight ?? 0]))

    const totalLoadedWeight = placements.reduce((sum, p) => {
      const rawId = (p as any).item_id || (p as any).itemId || ''
      const baseId = getBaseItemId(rawId, inputItemIds)
      return sum + (itemWeightMap.get(baseId) ?? 0)
    }, 0)

    if (totalLoadedWeight <= 0) return null
    return totalLoadedWeight / maxWeight
  }

  return loadedWeight / maxWeight
}

/**
 * Convertit la fraction de charge massique en pourcentage affiché.
 * Point d'entrée UNIQUE pour l'affichage — toujours appeler cette fonction,
 * jamais multiplier manuellement par 100.
 * Retourne null si la fraction est null.
 */
export function computeFillRateWeightPct(run: OptimizationRun): number | null {
  const fraction = computeFillRateWeight(run)
  if (fraction === null) return null
  return fraction * 100
}

/**
 * Résout l'ID parent d'un placement (ex: "11_1" → "11").
 */
export function getBaseItemId(placementItemId: string, inputItemIds: Set<string>, sourceId?: string): string {
  if (inputItemIds.has(placementItemId)) return placementItemId
  if (sourceId && inputItemIds.has(sourceId)) return sourceId
  const parts = placementItemId.split('_')
  for (let i = parts.length - 1; i >= 1; i--) {
    const candidate = parts.slice(0, i).join('_')
    if (inputItemIds.has(candidate)) return candidate
  }
  return placementItemId
}

/** Présentation commune des quantités : ID exact, source explicite, puis copies. */
export function buildArticlePresentation(run: OptimizationRun) {
  const items = run.input.items || []
  const ids = new Set(items.map(item => item.id))
  const groups = new Map((run.result?.palletGroups ?? run.result?.pallet_groups ?? []).map(g => [g.pallet_id, g]))
  const counts: Record<string, number> = {}
  for (const p of run.result?.placements || []) {
    const id = p.item_id ?? p.itemId ?? ''
    const group = groups.get(id)
    const children = group?.children_positions?.length
      ? group.children_positions.map(child => ({ id: child.item_id, source: child.source_id }))
      : group?.children_item_ids?.map(child => ({ id: child, source: undefined }))
        ?? [{ id, source: (p as any).source_id ?? (p as any).metadata?.source_id }]
    for (const child of children) {
      const source = getBaseItemId(child.id, ids, child.source)
      counts[source] = (counts[source] ?? 0) + 1
    }
  }
  const raw = (run.result as any)?.unloaded_items ?? (run.result as any)?.unloadedItems ?? []
  const unloaded = items.flatMap(item => {
    const remaining = Math.max(0, (item.quantity ?? 1) - (counts[item.id] ?? 0))
    if (!remaining) return []
    const reasons = raw.filter((u: any) => getBaseItemId(u.id ?? u.item_id ?? '', ids, u.metadata?.source_id) === item.id)
    return [{ ...item, remaining_quantity: remaining,
      reason: [...new Set(reasons.map((u: any) => u.reason ?? u.dominant_rejection_reason).filter(Boolean))].join(' ; '),
      reason_detail: [...new Set(reasons.map((u: any) => u.reason_detail ?? u.rejection_summary?.message).filter(Boolean))].join('\n')
    }]
  })
  // Garder les unités non résolues (par exemple palettes générées) visibles.
  for (const u of raw) {
    if (!ids.has(getBaseItemId(u.id ?? u.item_id ?? '', ids, u.metadata?.source_id))) {
      unloaded.push({ ...u, remaining_quantity: u.quantity ?? 1 })
    }
  }
  return { counts, unloaded }
}

/**
 * Formate un taux (fraction 0..1) en pourcentage affiché, ex: "63.2%"
 * Retourne "—" si la valeur est null.
 */
export function formatRate(rate: number | null): string {
  if (rate === null) return '—'
  return `${(rate * 100).toFixed(1)}%`
}

/**
 * Calcule le nombre total de colis/cartons individuels chargés.
 */
export function computeLoadedCartonsCount(run: OptimizationRun): number {
  if (!run.result) return 0
  const placements = run.result.placements ?? []
  const palletGroups = run.result.palletGroups ?? run.result.pallet_groups ?? []
  const palletGroupMap = new Map(palletGroups.map(pg => [pg.pallet_id, pg]))

  return placements.reduce((sum, p) => {
    const pId = p.item_id ?? p.itemId ?? ''
    const pg = palletGroupMap.get(pId)
    if (pg) {
      return sum + (pg.children_item_ids?.length || pg.children_positions?.length || 0)
    }
    return sum + 1
  }, 0)
}

/**
 * Calcule le nombre total de colis/cartons individuels demandés.
 */
export function computeTotalCartonsCount(run: OptimizationRun): number {
  if (!run.input?.items) return 0
  return run.input.items.reduce((sum, item) => sum + (item.quantity ?? 1), 0)
}

/**
 * Retourne la classe de couleur Tailwind pour les taux de remplissage (volume, surface).
 * Entrée attendue : fraction 0..1.
 */
export function getFillRateColor(rate: number | null, thresholds = { orange: 0.5, green: 0.8 }): string {
  if (rate === null || rate === undefined || isNaN(rate)) return 'bg-gray-200 dark:bg-gray-700'
  if (rate >= thresholds.green) return 'bg-green-500'
  if (rate >= thresholds.orange) return 'bg-orange-500'
  return 'bg-red-500'
}

/**
 * Retourne la classe de couleur Tailwind pour la CHARGE MASSIQUE.
 * La logique diffère de getFillRateColor : une charge élevée (proche de la limite)
 * est amber/rouge, une surcharge est rouge vif.
 * Entrée : fraction 0..N (peut dépasser 1 en surcharge).
 */
export function getFillRateWeightColor(fraction: number | null): string {
  if (fraction === null || fraction === undefined || isNaN(fraction)) return 'bg-gray-200 dark:bg-gray-700'
  if (fraction > 1.0) return 'bg-red-600'       // surcharge
  if (fraction > 0.95) return 'bg-red-500'      // à la limite
  if (fraction > 0.80) return 'bg-amber-500'    // chargement élevé
  if (fraction > 0.50) return 'bg-emerald-500'  // nominal
  return 'bg-slate-500'                          // chargement faible
}
